import { useCallback, useRef, useState } from 'react';
import {
  toFlatItems,
  deriveSortOrders,
  resolveProjectMembership,
  isFlatProject,
  isFlatLineItem,
  type FlatItem,
} from './types';
import type { BreakdownPatch, JobBreakdown } from '@jobos/shared';
import type { LineItemData, ProjectData } from './LineItemList';

// ============================================================================
// useFlatItems — central hook for flat DnD state management
// ============================================================================

export function useFlatItems() {
  const [flatItems, setFlatItemsRaw] = useState<FlatItem[]>([]);
  const [collapsedProjects, setCollapsedProjects] = useState<Set<string>>(new Set());

  // Mirror state in a ref so we can read it synchronously in methods
  const flatItemsRef = useRef<FlatItem[]>(flatItems);
  const setFlatItems = useCallback((updater: FlatItem[] | ((prev: FlatItem[]) => FlatItem[])) => {
    if (typeof updater === 'function') {
      setFlatItemsRaw((prev) => {
        const next = updater(prev);
        flatItemsRef.current = next;
        return next;
      });
    } else {
      flatItemsRef.current = updater;
      setFlatItemsRaw(updater);
    }
  }, []);

  // Refs for drag state (not rendered, so useRef avoids re-renders)
  const stashedMembers = useRef<FlatItem[]>([]);
  const preDragItems = useRef<FlatItem[]>([]);

  // --------------------------------------------------------------------------
  // initialize
  // --------------------------------------------------------------------------
  const initialize = useCallback((input: JobBreakdown | FlatItem[]) => {
    if (Array.isArray(input)) {
      setFlatItems(input);
    } else {
      setFlatItems(toFlatItems(input));
    }
  }, []);

  // --------------------------------------------------------------------------
  // moveItem — line item drag-end
  // --------------------------------------------------------------------------
  const moveItem = useCallback(
    (activeId: string, overItem: FlatItem, isCollapsedProject = false): { prev: FlatItem[]; next: FlatItem[] } | undefined => {
      const prev = flatItemsRef.current;
      const activeIndex = prev.findIndex((i) => i.id === activeId);
      if (activeIndex === -1) return undefined;

      // Dropping an item on its own slot (e.g. dragging back to the original
      // position) is a no-op. Without this guard, filtering out the active
      // item makes the over-index lookup return -1, and splice(-1, 0, …)
      // would insert before the LAST element — silently moving it to the
      // bottom of the list.
      if (overItem.id === activeId) return undefined;

      const activeItem = prev[activeIndex]!;

      let next: FlatItem[];

      if (overItem.type === 'project-header' && isCollapsedProject) {
        // Collapsed project: append after last member (drop INTO the project)
        const newProjectId = resolveProjectMembership(overItem, activeItem);
        const headerIndex = prev.findIndex((i) => i.id === overItem.id);
        if (headerIndex === -1) return undefined;

        // Find last member of this project
        let insertIndex = headerIndex + 1;
        while (insertIndex < prev.length && prev[insertIndex]?.projectId === overItem.id) {
          insertIndex++;
        }

        // Remove active item and insert at the right position
        next = prev.filter((i) => i.id !== activeId);
        // Adjust insert index if active was before the insert point
        const adjustedIndex = activeIndex < insertIndex ? insertIndex - 1 : insertIndex;
        const updatedItem: FlatItem = { ...activeItem, projectId: newProjectId };
        next.splice(adjustedIndex, 0, updatedItem);
      } else {
        // Standard case (including expanded project headers): insert BEFORE over item
        // This matches the blue line indicator which always renders BEFORE the over item
        // For expanded project headers, this places the item as loose before the project
        const newProjectId = resolveProjectMembership(overItem, activeItem);
        // If over item is an expanded project header, the item should be loose (before the project)
        const effectiveProjectId = overItem.type === 'project-header' ? null : newProjectId;

        const updatedItem: FlatItem = { ...activeItem, projectId: effectiveProjectId };
        next = prev.filter((i) => i.id !== activeId);
        const adjustedOverIndex = next.findIndex((i) => i.id === overItem.id);
        next.splice(adjustedOverIndex, 0, updatedItem);
      }

      setFlatItems(next);
      return { prev, next };
    },
    [],
  );

  // --------------------------------------------------------------------------
  // moveItemToEnd — append a line item to the very end of the list as a loose
  // item. Backs the end drop-zone, which makes the final position reachable
  // (the insert-before drop semantics otherwise leave it unreachable).
  // --------------------------------------------------------------------------
  const moveItemToEnd = useCallback(
    (activeId: string): { prev: FlatItem[]; next: FlatItem[] } | undefined => {
      const prev = flatItemsRef.current;
      const activeIndex = prev.findIndex((i) => i.id === activeId);
      if (activeIndex === -1) return undefined;

      const activeItem = prev[activeIndex]!;
      // Already the last item AND already loose → nothing to do.
      if (activeIndex === prev.length - 1 && activeItem.projectId === null) return undefined;

      const next = prev.filter((i) => i.id !== activeId);
      next.push({ ...activeItem, projectId: null });

      setFlatItems(next);
      return { prev, next };
    },
    [],
  );

  // --------------------------------------------------------------------------
  // moveProject — project header drag-end
  // --------------------------------------------------------------------------
  const moveProject = useCallback(
    (activeId: string, overItem: FlatItem): { prev: FlatItem[]; next: FlatItem[] } | undefined => {
      // Reject if overItem is inside another project
      if (overItem.projectId !== null) {
        return undefined;
      }

      const workingItems = flatItemsRef.current;
      const activeIndex = workingItems.findIndex((i) => i.id === activeId);
      const overIndex = workingItems.findIndex((i) => i.id === overItem.id);
      if (activeIndex === -1 || overIndex === -1) return undefined;

      // Self-drop (back to original position) is a no-op — same -1/splice
      // hazard as moveItem.
      if (overItem.id === activeId) return undefined;

      // Remove header, insert before over item (matches blue line position)
      const withoutActive = workingItems.filter((i) => i.id !== activeId);
      const adjustedOverIndex = withoutActive.findIndex((i) => i.id === overItem.id);
      const activeItem = workingItems[activeIndex]!;
      withoutActive.splice(adjustedOverIndex, 0, activeItem);

      // Re-insert stashed members after header at new position
      const headerIdx = withoutActive.findIndex((i) => i.id === activeId);
      const next = [
        ...withoutActive.slice(0, headerIdx + 1),
        ...stashedMembers.current,
        ...withoutActive.slice(headerIdx + 1),
      ];

      const prev = preDragItems.current;

      // Clear stashed state
      stashedMembers.current = [];
      preDragItems.current = [];

      setFlatItems(next);
      return { prev, next };
    },
    [],
  );

  // --------------------------------------------------------------------------
  // toggleCollapse
  // --------------------------------------------------------------------------
  const toggleCollapse = useCallback((projectId: string) => {
    setCollapsedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  }, []);

  // --------------------------------------------------------------------------
  // getVisibleSortableIds
  // --------------------------------------------------------------------------
  const getVisibleSortableIds = useCallback((): string[] => {
    return flatItems
      .filter((item) => {
        // Exclude members of collapsed projects
        if (item.type === 'line-item' && item.projectId !== null && collapsedProjects.has(item.projectId)) {
          return false;
        }
        return true;
      })
      .map((item) => item.id);
  }, [flatItems, collapsedProjects]);

  // --------------------------------------------------------------------------
  // startProjectDrag
  // --------------------------------------------------------------------------
  const startProjectDrag = useCallback((projectId: string) => {
    setFlatItems((prev) => {
      preDragItems.current = prev;
      stashedMembers.current = prev.filter(
        (i) => i.type === 'line-item' && i.projectId === projectId,
      );
      // Working items = flat array without stashed members
      return prev.filter(
        (i) => !(i.type === 'line-item' && i.projectId === projectId),
      );
    });
  }, []);

  // --------------------------------------------------------------------------
  // cancelProjectDrag
  // --------------------------------------------------------------------------
  const cancelProjectDrag = useCallback(() => {
    if (preDragItems.current.length > 0) {
      setFlatItems(preDragItems.current);
      stashedMembers.current = [];
      preDragItems.current = [];
    }
  }, []);

  // --------------------------------------------------------------------------
  // addLineItem
  // --------------------------------------------------------------------------
  const addLineItem = useCallback((item: LineItemData, projectId?: string, afterId?: string) => {
    const newItem: FlatItem = {
      id: item.id,
      type: 'line-item',
      projectId: projectId ?? null,
      data: item,
    };

    setFlatItems((prev) => {
      if (projectId) {
        // Find last member of the project (or the header if no members)
        let insertIndex = -1;
        for (let i = prev.length - 1; i >= 0; i--) {
          if (prev[i]?.id === projectId || prev[i]?.projectId === projectId) {
            insertIndex = i + 1;
            break;
          }
        }
        if (insertIndex === -1) {
          // Project not found, append to end
          return [...prev, newItem];
        }
        const next = [...prev];
        next.splice(insertIndex, 0, newItem);
        return next;
      }
      // Insert after a specific item if afterId is provided
      if (afterId) {
        const afterIndex = prev.findIndex((i) => i.id === afterId);
        if (afterIndex >= 0) {
          const next = [...prev];
          next.splice(afterIndex + 1, 0, newItem);
          return next;
        }
      }
      return [...prev, newItem];
    });
  }, []);

  // --------------------------------------------------------------------------
  // deleteLineItem
  // --------------------------------------------------------------------------
  const deleteLineItem = useCallback((id: string) => {
    setFlatItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  // --------------------------------------------------------------------------
  // updateLineItem
  // --------------------------------------------------------------------------
  const updateLineItem = useCallback(
    (id: string, updater: (data: LineItemData) => LineItemData) => {
      setFlatItems((prev) =>
        prev.map((item) =>
          item.id === id && isFlatLineItem(item)
            ? { ...item, data: updater(item.data as LineItemData) }
            : item,
        ),
      );
    },
    [],
  );

  // --------------------------------------------------------------------------
  // addProject
  // --------------------------------------------------------------------------
  const addProject = useCallback((project: ProjectData, afterId?: string) => {
    const newItem: FlatItem = {
      id: project.id,
      type: 'project-header',
      projectId: null,
      data: project,
    };
    setFlatItems((prev) => {
      if (afterId) {
        const afterIndex = prev.findIndex((i) => i.id === afterId);
        if (afterIndex >= 0) {
          const afterItem = prev[afterIndex]!;
          // If afterId is inside a project, insert after the entire project block
          if (afterItem.projectId !== null) {
            const parentProjectId = afterItem.projectId;
            let insertIndex = afterIndex + 1;
            while (insertIndex < prev.length && prev[insertIndex]?.projectId === parentProjectId) {
              insertIndex++;
            }
            const next = [...prev];
            next.splice(insertIndex, 0, newItem);
            return next;
          }
          // Loose item — insert right after it
          const next = [...prev];
          next.splice(afterIndex + 1, 0, newItem);
          return next;
        }
      }
      return [...prev, newItem];
    });
  }, []);

  // --------------------------------------------------------------------------
  // insertItems — bulk insert for paste operations
  // --------------------------------------------------------------------------
  const insertItems = useCallback((items: FlatItem[], afterId?: string) => {
    setFlatItems((prev) => {
      if (!afterId) {
        return [...prev, ...items];
      }
      const afterIndex = prev.findIndex((i) => i.id === afterId);
      if (afterIndex === -1) {
        return [...prev, ...items];
      }
      // If afterId is a project header, find end of that project's members
      const afterItem = prev[afterIndex]!;
      let insertIndex = afterIndex + 1;
      if (afterItem.type === 'project-header') {
        while (insertIndex < prev.length && prev[insertIndex]?.projectId === afterItem.id) {
          insertIndex++;
        }
      }
      const next = [...prev];
      next.splice(insertIndex, 0, ...items);
      return next;
    });
  }, []);

  // --------------------------------------------------------------------------
  // deleteProject — removes header, converts members to loose
  // --------------------------------------------------------------------------
  const deleteProject = useCallback((id: string) => {
    setFlatItems((prev) =>
      prev
        .filter((i) => !(i.type === 'project-header' && i.id === id))
        .map((i) => (i.projectId === id ? { ...i, projectId: null } : i)),
    );
  }, []);

  // --------------------------------------------------------------------------
  // renameProject
  // --------------------------------------------------------------------------
  const renameProject = useCallback((id: string, name: string) => {
    setFlatItems((prev) =>
      prev.map((item) =>
        item.id === id && isFlatProject(item)
          ? { ...item, data: { ...(item.data as ProjectData), name } }
          : item,
      ),
    );
  }, []);

  // --------------------------------------------------------------------------
  // getDndPatches — compare prev/next to emit patches
  // --------------------------------------------------------------------------
  const getDndPatches = useCallback(
    (prev: FlatItem[], next: FlatItem[]): BreakdownPatch[] => {
      const prevOrders = deriveSortOrders(prev);
      const nextOrders = deriveSortOrders(next);
      const patches: BreakdownPatch[] = [];

      // Build lookup for prev items by id
      const prevItemMap = new Map<string, FlatItem>();
      for (const item of prev) {
        prevItemMap.set(item.id, item);
      }

      for (const item of next) {
        const prevItem = prevItemMap.get(item.id);
        const prevOrder = prevOrders.get(item.id);
        const nextOrder = nextOrders.get(item.id);

        if (isFlatLineItem(item)) {
          const prevProjectId = prevItem?.projectId ?? null;
          const projectIdChanged = prevProjectId !== item.projectId;
          const sortOrderChanged = prevOrder !== nextOrder;

          if (projectIdChanged || sortOrderChanged) {
            patches.push({
              op: 'moveLineItem',
              id: item.id,
              projectId: item.projectId,
              sortOrder: nextOrder ?? 0,
            });
          }
        } else if (isFlatProject(item)) {
          const sortOrderChanged = prevOrder !== nextOrder;
          if (sortOrderChanged) {
            patches.push({
              op: 'updateProject',
              id: item.id,
              field: 'sortOrder',
              value: nextOrder ?? 0,
            });
          }
        }
      }

      return patches;
    },
    [],
  );

  return {
    flatItems,
    collapsedProjects,
    initialize,
    moveItem,
    moveItemToEnd,
    moveProject,
    toggleCollapse,
    getVisibleSortableIds,
    startProjectDrag,
    cancelProjectDrag,
    addLineItem,
    deleteLineItem,
    updateLineItem,
    addProject,
    insertItems,
    deleteProject,
    renameProject,
    getDndPatches,
  };
}
